import {getDateTime, moment} from "@/src/modules/core/lib";

import {BREAK_RULES, BREAK_RULE_DEFAULT} from "../lib";
import {ShiftSchema} from "../types";

class ShiftValidator {
  getShiftDuration(from_date: Date, from_time: string, to_date: Date, to_time: string) {
    if (!from_date || !from_time || !to_date || !to_time) {
      return 0;
    }
    const fromDateTime = getDateTime(from_date, from_time);
    const toDateTime = getDateTime(to_date, to_time);
    const shiftDuration = toDateTime.diff(fromDateTime, "hours", true);
    return shiftDuration;
  }

  getBreaksRule(from_date: Date, from_time: string, to_date: Date, to_time: string) {
    if (!from_date || !from_time || !to_date || !to_time) {
      return BREAK_RULE_DEFAULT;
    }

    const shiftDuration = this.getShiftDuration(from_date, from_time, to_date, to_time);
    const [_, rule] = BREAK_RULES.find(([duration]) => duration <= shiftDuration) ?? [
      0,
      BREAK_RULE_DEFAULT,
    ];
    return rule;
  }

  validateShiftRequiredBreaks(
    shift: ShiftSchema,
    required?: {rest_breaks: number; lunch_breaks: number},
  ) {
    const breaksRule = required
      ? {rest: required.rest_breaks, lunch: required.lunch_breaks}
      : this.getBreaksRule(shift.from_date, shift.from_time, shift.to_date, shift.to_time);
    const restBreaks = shift.breaks.filter((breakItem) => breakItem.break_type === "rest");
    const lunchBreaks = shift.breaks.filter((breakItem) => breakItem.break_type === "lunch");
    return restBreaks.length >= breaksRule.rest && lunchBreaks.length >= breaksRule.lunch;
  }

  validateBreaksDates(
    from_date: Date,
    from_time: string,
    to_date: Date,
    to_time: string,
    breaks: ShiftSchema["breaks"],
  ) {
    const isInvalid = breaks.some((breakItem, index) => {
      const otherBreaks = breaks.filter((_, i) => i !== index);
      const isBreakValid = shiftValidator.validateShiftBreakDates(
        from_date,
        from_time,
        to_date,
        to_time,
        breakItem.start_date,
        breakItem.start_time,
        breakItem.duration_minutes,
      );
      const isBreakOverlapping = shiftValidator.validateBreaksOverlaps(breakItem, otherBreaks);
      return !isBreakValid || isBreakOverlapping;
    });

    return !isInvalid;
  }

  validateShiftBreakDates(
    from_date: Date,
    from_time: string,
    to_date: Date,
    to_time: string,
    start_date: Date,
    start_time: string,
    duration_minutes: number,
  ) {
    const fromDateTime = getDateTime(from_date, from_time);
    const toDateTime = getDateTime(to_date, to_time);
    const breakDateTime = getDateTime(start_date, start_time);

    return (
      breakDateTime.isAfter(fromDateTime) &&
      breakDateTime.add(duration_minutes, "minutes").isBefore(toDateTime)
    );
  }

  validateBreaksOverlaps(breakItem: ShiftSchema["breaks"][number], breaks: ShiftSchema["breaks"]) {
    const breakStartDateTime = getDateTime(breakItem.start_date, breakItem.start_time);
    const breakEndDateTime = moment(breakStartDateTime).add(breakItem.duration_minutes, "minutes");
    return breaks.some((otherBreak) => {
      const otherBreakStartDateTime = getDateTime(otherBreak.start_date, otherBreak.start_time);
      const otherBreakEndDateTime = moment(otherBreakStartDateTime).add(
        otherBreak.duration_minutes,
        "minutes",
      );

      return (
        (breakStartDateTime.isSameOrAfter(otherBreakStartDateTime) &&
          breakStartDateTime.isSameOrBefore(otherBreakEndDateTime)) ||
        (breakEndDateTime.isSameOrAfter(otherBreakStartDateTime) &&
          breakEndDateTime.isSameOrBefore(otherBreakEndDateTime))
      );
    });
  }
}

export const shiftValidator = new ShiftValidator();
